/**
 * CreateSiteDialog — quick-create dialog for new sites.
 *
 * Mirrors EditSiteDialog's visual structure (tabbed General / Extra
 * Details, Settings* shared form components, sectioned Formik values)
 * so the two surfaces look and feel identical. The submit step
 * flattens the sectioned form back to the legacy createSite payload
 * shape (the create endpoint isn't on the consolidated /site-settings/
 * route — only edit reads from there).
 */

import { TabContext, TabPanel } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { useUser } from '@sophocles/app';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import { useCreateSiteMutation } from '@sophocles/features/entities/site';
import { selectUnitModels } from '@sophocles/features/reference';
import { addToast } from '@sophocles/features/toast';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import {
  SettingsFieldGroup,
  SettingsTextField,
  SettingsTimeZoneField,
  UseDeviceLocationButton,
} from '@sophocles/views/settings/components';

// The API reports validation failures against the flat site payload
// (e.g. "name"), but this form's Formik values are sectioned
// (general.name, location.latitude, ...) — no flat field name lines up
// with an input, so every message surfaces in the banner instead of inline.
const KNOWN_FIELDS = [];

const buildValidationSchema = ({ requireBilledSiteGroup, t }) =>
  yup.object({
    general: yup.object({
      name: yup.string().trim().required(t('validation.nameRequiredPeriod', 'Name is required.')),
      description: yup.string().nullable(),
      location_description: yup.string().nullable(),
    }),
    location: yup.object({
      latitude: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError(t('validation.latitudeMustBeNumber', 'Latitude must be a number.'))
        .required(t('validation.latitudeRequired', 'Latitude is required.')),
      longitude: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError(t('validation.longitudeMustBeNumber', 'Longitude must be a number.'))
        .required(t('validation.longitudeRequired', 'Longitude is required.')),
      time_zone: yup.string().nullable(),
    }),
    utility: yup.object({
      tag_number: yup.string().nullable(),
      utility_meter_number: yup.string().nullable(),
      utility_company: yup.string().nullable(),
      utility_account_number: yup.string().nullable(),
    }),
    billed_site_group: requireBilledSiteGroup ? yup.number().required() : yup.number().nullable(),
    model: yup.number().nullable(),
  });

// Smartlink model selector (staff-only). Lives in the General tab
// underneath GPS so users see the choice before they pick coords.
function ModelSelector() {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const unitModels = useSelector(selectUnitModels);

  const options = unitModels
    .filter(m => !m.is_obsolete || m.id === values.model)
    .map(m => ({ id: m.id, label: m.display_name }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

  return (
    <Autocomplete
      options={options}
      getOptionLabel={option => option.label || ''}
      value={options.find(o => o.id === values.model) || null}
      onChange={(_event, option) => setFieldValue('model', option ? option.id : '')}
      renderInput={params => (
        <TextField
          {...params}
          label={t('sites.deviceModel', 'Device Model')}
          helperText={t('sites.deviceModelHelper', '')}
          size="small"
          fullWidth
        />
      )}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
    />
  );
}

// Billed-site-group selector (only when the parent group requires
// alternate billing). The inner SiteGroupAutocomplete is uncontrolled
// from Formik so we wire value/onChange manually.
function BilledSiteGroupField({ siteGroupId, customerId, required }) {
  const { t } = useTranslation();
  const siteGroupTerm = useSiteGroupTerm();
  const { values, errors, touched, setFieldValue } = useFormikContext();

  return (
    <SiteGroupAutocomplete
      excludeInventory
      value={values.billed_site_group}
      fullWidth
      required={required}
      customerId={customerId}
      ignoreIds={siteGroupId ? [siteGroupId] : []}
      label={t('general.billedEntity', {
        entity: siteGroupTerm.singular,
      })}
      filters={{ is_virtual: false }}
      error={Boolean(touched.billed_site_group && errors.billed_site_group)}
      helperText={touched.billed_site_group && errors.billed_site_group}
      onChange={(_e, billedSiteGroupId) => setFieldValue('billed_site_group', billedSiteGroupId)}
    />
  );
}

function CreateSiteDialog({ open, onClose, onSuccess, siteGroup, customerId, showExtraDetails = true }) {
  const { t } = useTranslation();
  const siteTerm = useSiteTerm();
  const dispatch = useDispatch();
  const { isStaff } = useUser();
  const [currentTab, setCurrentTab] = useState('0');
  const [nonFieldMessages, setNonFieldMessages] = useState([]);
  const [createSite] = useCreateSiteMutation();

  useEffect(() => {
    if (open) {
      setNonFieldMessages([]);
    }
  }, [open]);

  const siteGroupId = siteGroup?.get?.('id') ?? siteGroup?.id;
  const requireBilledSiteGroup = siteGroup?.toJS?.()?.options?.require_sites_bill_alternate ?? false;

  const initialValues = {
    general: {
      name: '',
      description: '',
      location_description: '',
    },
    location: {
      latitude: '',
      longitude: '',
      time_zone: moment.tz.guess(),
    },
    utility: {
      tag_number: '',
      utility_meter_number: '',
      utility_company: '',
      utility_account_number: '',
    },
    billed_site_group: null,
    model: '',
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setNonFieldMessages([]);
    const sitePayload = {
      name: values.general.name,
      description: values.general.description,
      location_description: values.general.location_description,
      latitude: values.location.latitude ? parseFloat(values.location.latitude).toFixed(8) : null,
      longitude: values.location.longitude ? parseFloat(values.location.longitude).toFixed(8) : null,
      time_zone: values.location.time_zone,
      primary_site_group: siteGroupId,
      billed_site_group: values.billed_site_group,
      configuration: {
        tag_number: values.utility.tag_number,
        utility_meter_number: values.utility.utility_meter_number,
        utility_company: values.utility.utility_company,
        utility_account_number: values.utility.utility_account_number,
      },
      ...(values.model ? { relay_controller: { model: values.model } } : {}),
    };

    try {
      const result = await createSite({ body: sitePayload });
      if (result.error) {
        setNonFieldMessages(
          applyApiErrorToFormik(result.error, {
            setErrors,
            knownFields: KNOWN_FIELDS,
            genericMessage: t('errors.unexpected', 'Something went wrong. Please try again.'),
          }),
        );
        setSubmitting(false);
        return;
      }
      const newSite = result.data;
      dispatch(
        addToast({
          title: t('general.success', 'Success'),
          message: `${siteTerm.singular} ${newSite.name} ${t('general.created', 'Created')}`,
          level: 'success',
          dismissible: true,
        }),
      );
      onClose();
      if (onSuccess) onSuccess(newSite, sitePayload);
    } catch (error) {
      setNonFieldMessages(
        applyApiErrorToFormik(error, {
          setErrors,
          knownFields: KNOWN_FIELDS,
          genericMessage: t('errors.unexpected', 'Something went wrong. Please try again.'),
        }),
      );
      setSubmitting(false);
    }
  };

  const handleTabChange = (_event, value) => setCurrentTab(value);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '85vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        {t('actions.create')} {siteTerm.singular}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={buildValidationSchema({ requireBilledSiteGroup, t })}
        onSubmit={handleSubmit}
      >
        {({ submitForm, isSubmitting, dirty, isValid }) => (
          <Form>
            <FormErrorAlert messages={nonFieldMessages} sx={{ mx: 3, mt: 1 }} />
            <TabContext value={currentTab}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
              >
                <Tab label={t('sites.generalDetails')} value="0" />
                {showExtraDetails && <Tab label={t('sites.extraDetails')} value="1" />}
              </Tabs>

              <DialogContent dividers sx={{ p: 0 }}>
                <TabPanel value="0" sx={{ px: 3 }}>
                  <Stack spacing={2}>
                    <SettingsTextField name="general.name" label={t('general.name', 'Name')} required />
                    <SettingsTextField
                      name="general.description"
                      label={t('general.description', 'Description')}
                      multiline
                      minRows={2}
                    />
                    <SettingsTextField
                      name="general.location_description"
                      label={t('siteSettings.locationDescription', 'Location Description')}
                      multiline
                      minRows={2}
                    />
                    <SettingsTimeZoneField name="location.time_zone" label={t('general.timeZone', 'Time Zone')} />
                    <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
                      <SettingsTextField name="location.latitude" label={t('general.latitude', 'Latitude')} required />
                      <SettingsTextField
                        name="location.longitude"
                        label={t('general.longitude', 'Longitude')}
                        required
                      />
                    </SettingsFieldGroup>
                    <Box>
                      <UseDeviceLocationButton />
                    </Box>

                    {isStaff && <ModelSelector />}

                    {requireBilledSiteGroup && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                          {t('general.billing')}
                        </Typography>
                        <BilledSiteGroupField siteGroupId={siteGroupId} customerId={customerId} required />
                      </Box>
                    )}
                  </Stack>
                </TabPanel>

                {showExtraDetails && (
                  <TabPanel value="1" sx={{ px: 3 }}>
                    <Stack spacing={2}>
                      <SettingsFieldGroup columns={{ xs: 1, sm: 2 }}>
                        <SettingsTextField
                          name="utility.tag_number"
                          label={t('siteSettings.tagNumber', 'Tag Number')}
                        />
                        <SettingsTextField
                          name="utility.utility_company"
                          label={t('siteSettings.utilityCompany', 'Utility Company')}
                        />
                        <SettingsTextField
                          name="utility.utility_meter_number"
                          label={t('siteSettings.utilityMeterNumber', 'Utility Meter Number')}
                        />
                        <SettingsTextField
                          name="utility.utility_account_number"
                          label={t('siteSettings.utilityAccountNumber', 'Utility Account Number')}
                        />
                      </SettingsFieldGroup>
                    </Stack>
                  </TabPanel>
                )}
              </DialogContent>
            </TabContext>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={onClose} variant="outlined">
                {t('actions.cancel')}
              </Button>
              <Button onClick={submitForm} variant="contained" disabled={isSubmitting || !(isValid && dirty)}>
                {t('actions.create')}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default CreateSiteDialog;
